import { isRunningApp } from './appStatus.ts';

export type ContainerAction = 'start' | 'stop' | 'restart';

/** True for statuses where the container is live (stats are collected). Same set as apps. */
export function isRunningContainer(status: string | null | undefined): boolean {
  return isRunningApp(status);
}

/**
 * Actions offered for a container status. The bridge does not validate status (it proxies to Docker via
 * Portainer, and any non-2xx, including Docker's 304 "already started/stopped", surfaces as an error),
 * so these rules only offer actions Docker accepts: start only for non-running containers (Docker rejects
 * starting a paused one), stop/restart for live ones. There is no unpause action; restart resumes a
 * paused container (verified against a local Docker daemon), so paused offers restart and stop.
 */
export function containerActions(status: string | null | undefined): ContainerAction[] {
  const s = (status ?? '').trim().toLowerCase();
  if (isRunningContainer(s)) return ['restart', 'stop'];
  switch (s) {
    case 'paused':
      return ['restart', 'stop'];
    case 'restarting':
      return ['stop'];
    case 'stopped':
    case 'unknown':
      return ['start'];
    default:
      return [];
  }
}

/** Stop and restart interrupt the workload and need confirmation; start does not. */
export function isDisruptiveContainerAction(a: ContainerAction): a is 'stop' | 'restart' {
  return a === 'stop' || a === 'restart';
}
